import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Profile.css";

const ProfilePage = () => {
  const [userData, setUserData] = useState(null);
  const [profileError, setProfileError] = useState("");
  const [userAddress, setUserAddress] = useState("");
  const [userOrganization, setUserOrganization] = useState("");
  const [isProfileSaving, setIsProfileSaving] = useState(false);
  const [isProfileEditing, setIsProfileEditing] = useState(false); 
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserProfileData = async () => {
      const authToken = localStorage.getItem("token");

      if (!authToken) {
        navigate("/login");
        return;
      }

      try {
        const profileResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        if (!profileResponse.ok) {
          throw new Error("Failed to fetch user profile data.");
        }

        const profileData = await profileResponse.json();
        if (profileData.user) {
          setUserData(profileData.user);
          setUserAddress(profileData.user.address || "");
          setUserOrganization(profileData.user.organization || "");
        } else {
          throw new Error("Invalid user profile data.");
        }
      } catch (error) {
        setProfileError("Session expired. Please log in again.");
        localStorage.removeItem("token");
        navigate("/login");
      }
    };

    fetchUserProfileData();
  }, [navigate]);

  const handleProfileSave = async () => {
    setIsProfileSaving(true);
    const authToken = localStorage.getItem("token");

    if (!authToken) {
      setProfileError("Session expired. Please log in again.");
      localStorage.removeItem("token");
      navigate("/login");
      return;
    }

    try {
      const updateResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/updateProfile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          address: userAddress,
          organization: userOrganization,
        }),
      });

      if (!updateResponse.ok) {
        throw new Error("Failed to update user profile.");
      }

      const updateData = await updateResponse.json();
      if (updateData.success) {
        setUserData({ ...userData, address: userAddress, organization: userOrganization });
        setIsProfileEditing(false); 
        setProfileError("");
      } else {
        throw new Error("Error updating user profile.");
      }
    } catch (error) {
      setProfileError(error.message);
    } finally {
      setIsProfileSaving(false);
    }
  };

  const handleProfileEdit = () => {
    setIsProfileEditing(true); 
  };

  const handleProfileCancel = () => {
    setIsProfileEditing(false); 
    setUserAddress(userData.address || ""); 
    setUserOrganization(userData.organization || "");
  };

  return (
    <div className="profile-page-container">
      <div className="profile-page-card">
        <h2 className="profile-page-title">User Profile</h2>
        {profileError && <p className="profile-page-error">{profileError}</p>}
        {userData ? (
          <div className="profile-page-content">
            <div className="profile-page-info-item">
              <span className="profile-page-info-label">Full Name:</span>
              <span className="profile-page-info-value">{userData.name}</span>
            </div>

            <div className="profile-page-info-item">
              <span className="profile-page-info-label">Email:</span>
              <span className="profile-page-info-value">{userData.email}</span>
            </div>

            <div className="profile-page-field-group">
              <label htmlFor="profileAddress" className="profile-page-field-label">Address:</label>
              <input
                type="text"
                id="profileAddress"
                className="profile-page-field-input"
                value={userAddress}
                onChange={(e) => setUserAddress(e.target.value)}
                disabled={!isProfileEditing}
              />
            </div>

            <div className="profile-page-field-group">
              <label htmlFor="profileOrganization" className="profile-page-field-label">Organization:</label>
              <input
                type="text"
                id="profileOrganization"
                className="profile-page-field-input"
                value={userOrganization}
                onChange={(e) => setUserOrganization(e.target.value)}
                disabled={!isProfileEditing}
              />
            </div>

            {isProfileEditing ? (
              <div className="profile-page-action-buttons">
                <button
                  onClick={handleProfileSave}
                  disabled={isProfileSaving}
                  className="profile-page-save-btn"
                >
                  {isProfileSaving ? (
                    <span className="profile-page-btn-loading">
                      <span className="profile-page-btn-spinner"></span>
                      Saving...
                    </span>
                  ) : (
                    "Save Changes"
                  )}
                </button>
                <button
                  onClick={handleProfileCancel}
                  className="profile-page-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={handleProfileEdit}
                className="profile-page-edit-btn"
              >
                Edit Profile
              </button>
            )}
          </div>
        ) : (
          <div className="profile-page-loading">
            <div className="profile-page-loading-spinner"></div>
            <p>Loading profile data...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;